import React from 'react';
import { Link } from 'react-router-dom';
import { CircleDollarSignIcon, SearchXIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { ProvenanceBadge } from '../../trust/ProvenanceBadge';
import { SaveButton } from '../SaveButton';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { deadlineState } from '../../../utils/discover';
import { cn } from '../../../utils/cn';

/**
 * Funding results. The deadline reading leads, because with an award the date is
 * the thing that decides whether it is worth reading at all.
 */
interface ScholarshipResultsProps {
  density: 'editorial' | 'compact';
}
export function ScholarshipResults({
  density
}: ScholarshipResultsProps) {
  const {
    scholarships,
    isSaved,
    toggleSave,
    clearFilters
  } = useDiscover();
  if (scholarships.length === 0) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-1637" data-mp="3potpna5qi25h">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1639" data-mp="18jibl94t03ee" />} title="No awards match what you asked for" description="Campuzon lists awards published by funders and institutions, plus ones students have contributed. Widen the level or deadline to see the rest." action={<Button variant="secondary" onClick={clearFilters} data-id="element-1640" data-mp="1dkxy75mmqhyw">
              Clear all filters
            </Button>} data-id="element-1638" data-mp="18ka7kl2bc357" />
      </div>;
  }
  return <ul className={cn('grid gap-4', density === 'compact' ? 'md:grid-cols-2' : 'lg:grid-cols-2')} data-id="element-1641" data-mp="kvha2k61ln8h">
      {scholarships.map(scholarship => {
      const state = deadlineState(scholarship.deadlineLabel);
      return <li key={scholarship.id} className="flex flex-col rounded-card border border-line bg-surface p-5 transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-[0_10px_30px_rgba(15,23,42,0.07)]" data-id="element-1642" data-mp="tvnme2q08h62">
            <div className="flex items-start gap-3" data-id="element-1643" data-mp="218g894wcroyl">
              <span aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50" data-id="element-1644" data-mp="268koqux9gjot">
                <CircleDollarSignIcon className="h-5 w-5 text-brand-700" data-id="element-1645" data-mp="2b02oxgrgmg8q" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-1646" data-mp="2fxui0e3uc8ei">
                <h3 className="text-body font-semibold text-ink" data-id="element-1647" data-mp="1iavg9oyaka9r">
                  <Link to={`/discover/scholarships/${scholarship.id}`} className="rounded outline-none hover:underline focus-visible:underline" data-id="element-1648" data-mp="1icq3j9x1ceh0">
                    {scholarship.name}
                  </Link>
                </h3>
                <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-1649" data-mp="1x97e3yxehp2i">
                  {scholarship.funder} · {scholarship.level}
                </p>
              </div>
              <SaveButton saved={isSaved('scholarship', scholarship.id)} onToggle={() => toggleSave('scholarship', scholarship.id)} subject={scholarship.name} data-id="element-1650" data-mp="38m02czqzfvdt" />
            </div>

            <p className="cz-num mt-4 text-h3 font-bold tracking-tight text-ink" data-id="element-1651" data-mp="s2plkhcd8tem">
              {scholarship.amountLabel}
            </p>
            <p className="text-body-sm text-ink-muted" data-id="element-1652" data-mp="ibbglu1nb1e3">{scholarship.coverage}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1653" data-mp="dbqifluaux2f">
              <Badge tone={state.tone === 'neutral' ? 'neutral' : state.tone} plain data-id="element-1654" data-mp="dd1m7lnqw4tj">
                {state.closed ? 'Closed' : state.label}
              </Badge>
              <Badge plain data-id="element-1655" data-mp="4c3easa6rd5o">{scholarship.deadlineLabel}</Badge>
              <ProvenanceBadge provenance={scholarship.provenance} short data-id="element-1656" data-mp="3vhbky6inztcc" />
            </div>

            <ul className="mt-4 space-y-1.5 border-t border-line pt-3" data-id="element-1657" data-mp="3vjo7yy3a4tw9">
              {scholarship.eligibility.map(item => <li key={item} className="text-body-sm text-ink-muted" data-id="element-1658" data-mp="1tpgkkn1uzb9f">
                  · {item}
                </li>)}
            </ul>

            <p className="mt-3 text-caption text-ink-subtle" data-id="element-1659" data-mp="rag1ghueqq67">{scholarship.sourceUrlLabel}</p>

            <div className="mt-auto pt-4" data-id="element-1660" data-mp="vdcmsgbc10du">
              <Link to={`/discover/scholarships/${scholarship.id}`} data-id="element-1661" data-mp="hew7i0zem1k3">
                <Button size="sm" variant="primary" data-id="element-1662" data-mp="hgqstgel7vop">
                  View scholarship
                </Button>
              </Link>
            </div>
          </li>;
    })}
    </ul>;
}